import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router } from '@angular/router';
import { filter, map, of, switchMap, take } from 'rxjs';
import { ModulesStore } from '../../../features/admin/modules/modules.store';
import { toObservable } from '@angular/core/rxjs-interop';
import { UserStore } from 'app/core/services/user.store';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { AuthService } from 'app/core/auth/auth.service';

export const CanViewGuard: CanActivateFn | CanActivateChildFn = route => {
  const userStore = inject(UserStore);
  const modulesStore = inject(ModulesStore);
  const confirmationService = inject(FuseConfirmationService);
  const authService = inject(AuthService);
  const router = inject(Router);

  const loaded = toObservable(modulesStore.modulesLoaded);
  const currentUser = toObservable(userStore.currentUser);
  const currentUserPermissions = toObservable(userStore.currentUserPermissions);

  if (!modulesStore.modulesLoaded()) {
    modulesStore.loadModules(undefined);
  }

  return loaded.pipe(
    filter(value => value === true),
    take(1),
    switchMap(() => {
      // Ensure user data is loaded
      userStore.ensureLoaded();

      const moduleSlug = route.paramMap.get('name');
      const module = modulesStore.enabledModules().find(module => module.slug === moduleSlug);
      // Check if the user has read permission for this module
      if (module?.isPublic) {
        // If the module is public, allow access directly
        return of(true);
      }

      return currentUserPermissions.pipe(
        filter(permissions => permissions !== null && permissions !== undefined && permissions?.length > 0),
        take(1),
        switchMap(() => {
          return currentUser.pipe(
            take(1),
            map(currentUser => {
              if (userStore.isOnboarding() && moduleSlug != 'onboarding') {
                return router.parseUrl('/onboarding');
              }

              if (userStore.checkIfHasReadPermission(module?.id)) {
                return true;
              } else {
                console.warn('Je hebt geen toegang tot deze pagina ' + module?.id + ' ' + module?.slug);

                if (userStore.impersonationData()) {
                  // User is impersonating but the impersonated user lacks access
                  const impersonatedName = userStore.currentUser()?.name || 'huidige gebruiker';

                  const confirmation = confirmationService.open({
                    title: 'Geen toegangsrechten',
                    message: `Je bent momenteel ingelogd als ${impersonatedName} maar deze gebruiker heeft geen toegang tot deze pagina. Wat wil je doen?`,
                    icon: {
                      show: true,
                      name: 'heroicons_outline:lock-closed',
                      color: 'warn',
                    },
                    actions: {
                      confirm: {
                        show: true,
                        label: 'Stop impersonatie',
                        color: 'primary',
                      },
                      cancel: {
                        show: true,
                        label: 'Uitloggen',
                      },
                    },
                    dismissible: false,
                  });

                  confirmation.afterClosed().subscribe(result => {
                    if (result === 'confirmed') {
                      userStore.stopImpersonating();
                    } else if (result === 'cancelled') {
                      authService.signOut();
                      router.navigate(['/home']);
                    }
                  });
                } else {
                  return router.parseUrl('/no-access');
                }
                return false;
              }
            })
          );
        })
      );
    })
  );
};
